import { FC } from "react";
import { PanelBottom, PanelRight, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import SearchButton from "./SearchButton";
import { SidebarTab } from "../../../types";
import { PyodideState } from "../../services/PyodideService";
import { EditorLoadingStage } from "../../stores/UIStore";
import { observer } from "mobx-react-lite";
import { rootStore } from "../../stores/RootStore";

// Add global styles
const globalStyles = `
    @keyframes size-pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.08); }
        100% { transform: scale(1); }
    }
`;

interface TopBarProps {
    logsVisible?: boolean;
    agentVisible?: boolean;
    onNewProject?: () => void;
    onOpenProject?: () => void;
    onOpenPreview?: () => void;
    setSelectedFile?: (filePath: string) => void;
    activeTab?: SidebarTab;
    setActiveTab?: (tab: SidebarTab) => void;
    projectId?: string | null;
    isPyodideLoaded?: boolean;
    pyodideState?: PyodideState;
    pyodideLoadingStatus?: string;
}

const TopBar: FC<TopBarProps> = observer(({
    logsVisible = false,
    agentVisible = true,
    onNewProject,
    onOpenProject,
    onOpenPreview,
    setSelectedFile,
    activeTab,
    setActiveTab,
    projectId,
    isPyodideLoaded = false,
    pyodideState = PyodideState.UNINITIALIZED,
    pyodideLoadingStatus = "",
}) => {
    const { uiStore } = rootStore;

    const getPyodideStatusInfo = () => {
        // Use MobX uiStore.pyodideLoadingStatus if available
        const status = uiStore.pyodideLoadingStatus || pyodideLoadingStatus;

        switch (pyodideState) {
            case PyodideState.READY:
                return {
                    dotColor: '#4ade80',
                    text: 'Environment Ready'
                };
            case PyodideState.LOADING:
                // Show more detailed loading status if available
                if (status === 'loading-pyodide') {
                    return {
                        dotColor: '#facc15',
                        text: 'Loading Python'
                    };
                } else if (status === 'installing-packages') {
                    return {
                        dotColor: '#facc15',
                        text: 'Installing Packages'
                    };
                } else if (status === 'loading-files') {
                    return {
                        dotColor: '#facc15',
                        text: 'Loading Files'
                    };
                } else if (status === 'clearing-filesystem') {
                    return {
                        dotColor: '#facc15',
                        text: 'Clearing Filesystem'
                    };
                } else if (status === 'running-script') {
                    return {
                        dotColor: '#facc15',
                        text: 'Running Script'
                    };
                } else {
                    return {
                        dotColor: '#facc15',
                        text: 'Initializing Environment'
                    };
                }
            case PyodideState.ERROR:
                return {
                    dotColor: '#f87171',
                    text: 'Environment Error'
                };
            default:
                return {
                    dotColor: '#d1d5db',
                    text: 'Environment Starting'
                };
        }
    };

    const statusInfo = getPyodideStatusInfo();

    return (
        <>
            <style>{globalStyles}</style>
            <div className="flex items-center justify-between h-10 px-4 shrink-0 border-b border-gray-200">
                <div className="flex items-center gap-4">
                    <Button
                        onClick={onOpenProject}
                        variant="ghost"
                        size="sm"
                        className="flex items-center gap-2 h-7 px-3 text-sm text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    {projectId && (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                            {(pyodideState === PyodideState.LOADING || uiStore.loadingStage === EditorLoadingStage.INITIALIZING_PYODIDE) ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-yellow-500" />
                            ) : (
                                <div
                                    className="h-3.5 w-3.5 rounded-full"
                                    style={{ 
                                        backgroundColor: statusInfo.dotColor,
                                        transformOrigin: 'center',
                                        animation: 'size-pulse 3s ease-in-out infinite'
                                    }}
                                />
                            )}
                            <span>{statusInfo.text}</span>
                        </div>
                    )}
                </div>

                <div className="flex-1 flex justify-center">
                    <SearchButton
                        onNewProject={onNewProject}
                        onOpenProject={onOpenProject}
                        onOpenPreview={onOpenPreview}
                        setSelectedFile={setSelectedFile}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                    />
                </div>

                <div className="flex items-center gap-2">
                    <Button 
                        onClick={() => window.dispatchEvent(new CustomEvent('toggleLogsPanel'))} 
                        variant="ghost"
                        size="sm" 
                        className={`h-6 w-6 p-0 rounded-md ${logsVisible ? "bg-gray-200" : ""}`} 
                        title="Toggle logs panel (⌘J)"
                    >
                        <PanelBottom className="h-3.5 w-3.5" />
                    </Button>
                    <Button 
                        onClick={() => window.dispatchEvent(new CustomEvent('toggleAgentPanel'))} 
                        variant="ghost"
                        size="sm" 
                        className={`h-6 w-6 p-0 rounded-md ${agentVisible ? "bg-gray-200" : ""}`}
                        title="Toggle agent panel (⌘I)"
                    >
                        <PanelRight className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>
        </>
    );
});

export default TopBar;
